import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  STATUS_COLORS,
  DEFAULT_STATUS,
  fmtDate,
  fmtTime,
} from "../Logs/logHelpers";

const FONT_HEADING = '"Inter", system-ui, sans-serif';

// Problem states get their notes auto-expanded so a user lands straight on the
// reason a sync stalled — that's the whole point of the per-record journey.
const PROBLEM_STATUSES = new Set(["Failed", "Error", "Partial", "Retrying"]);

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || DEFAULT_STATUS;
  return (
    <Box
      component="span"
      sx={{
        fontSize: 12,
        fontWeight: 600,
        px: "8px",
        py: "2px",
        borderRadius: "9999px",
        bgcolor: c.bg,
        color: c.color,
        border: `1.5px solid ${c.border}`,
        whiteSpace: "nowrap",
      }}
    >
      {status || "Unknown"}
    </Box>
  );
}

/** Pretty-print a JSON request body, falling back to the raw string. */
const prettyBody = (body) => {
  try {
    return JSON.stringify(JSON.parse(body), null, 2);
  } catch {
    return body;
  }
};

/**
 * Vertical, chronological timeline of a single record's Shopify_Logs entries.
 * Presentational only — the page resolves the match keys, fetches, normalises
 * and sorts (oldest → newest) the `events` before handing them here.
 */
export default function JourneyTimeline({ events = [] }) {
  const problemIds = useMemo(
    () => events.filter((e) => PROBLEM_STATUSES.has(e.status)).map((e) => e.id),
    [events]
  );

  const [expanded, setExpanded] = useState(() => new Set());
  // Seed the auto-expanded problem rows whenever the event set changes.
  useEffect(() => {
    setExpanded(new Set(problemIds));
  }, [problemIds]);

  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  return (
    <Box sx={{ position: "relative" }}>
      {/* Summary line */}
      <Box sx={{ fontSize: 13, color: "#71717a", mb: "14px" }}>
        {events.length} sync event{events.length !== 1 ? "s" : ""} · oldest first
      </Box>

      <Box sx={{ position: "relative", pl: "8px" }}>
        {events.map((ev, idx) => {
          const c = STATUS_COLORS[ev.status] || DEFAULT_STATUS;
          const isOpen = expanded.has(ev.id);
          const isLast = idx === events.length - 1;
          const direction = [ev.fromPlatform, ev.toPlatform].filter(Boolean).join("  →  ");

          return (
            <Box key={ev.id || idx} sx={{ display: "flex", gap: "12px", position: "relative" }}>
              {/* Rail: dot + connecting line */}
              <Box sx={{ position: "relative", width: 16, flexShrink: 0 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: c.color,
                    border: "2px solid #fff",
                    boxShadow: `0 0 0 2px ${c.border}`,
                    mt: "4px",
                    zIndex: 1,
                    position: "relative",
                  }}
                />
                {!isLast && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "18px",
                      bottom: "-10px",
                      left: "5px",
                      width: "2px",
                      bgcolor: "#e4e4e7",
                    }}
                  />
                )}
              </Box>

              {/* Card */}
              <Box
                sx={{
                  flex: 1,
                  mb: "16px",
                  border: "1px solid #e4e4e7",
                  borderRadius: "10px",
                  bgcolor: "#fff",
                  overflow: "hidden",
                }}
              >
                <Box
                  onClick={() => toggle(ev.id)}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "10px",
                    p: "10px 12px",
                    cursor: "pointer",
                    "&:hover": { bgcolor: "#fafafa" },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Timestamp + direction */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", mb: "4px" }}>
                      <Box sx={{ fontSize: 14, fontWeight: 600, color: "#09090b", fontFamily: FONT_HEADING }}>
                        {fmtDate(ev.createdTime)}
                      </Box>
                      <Box sx={{ fontSize: 13, color: "#a1a1aa", fontFamily: "monospace" }}>
                        {fmtTime(ev.createdTime)}
                      </Box>
                      {direction && (
                        <Box sx={{ fontSize: 12, color: "#52525b", bgcolor: "#f4f4f5", px: "8px", py: "2px", borderRadius: "9999px", whiteSpace: "nowrap" }}>
                          {direction}
                        </Box>
                      )}
                    </Box>
                    {/* Event name */}
                    <Box sx={{ fontSize: 13, fontFamily: "monospace", color: "#3f3f46", wordBreak: "break-word" }}>
                      {ev.name || ev.shopifyModule || "—"}
                    </Box>
                  </Box>

                  <StatusBadge status={ev.status} />
                  <IconButton size="small" sx={{ p: "2px", color: "#71717a" }}>
                    {isOpen ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                </Box>

                {/* Expanded detail: notes (Reason) + request body */}
                {isOpen && (
                  <Box sx={{ borderTop: "1px solid #f4f4f5", p: "12px", bgcolor: "#fafafa" }}>
                    {/* Meta row */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "16px", mb: "10px" }}>
                      {ev.shopifyModule && <Meta label="Shopify Module" value={ev.shopifyModule} />}
                      {ev.shopifyRecordId && <Meta label="Shopify ID" value={ev.shopifyRecordId} mono />}
                      {ev.module && <Meta label="Zoho Module" value={ev.module} />}
                      {ev.moduleRecordId && <Meta label="Record ID" value={ev.moduleRecordId} mono />}
                    </Box>

                    <SectionLabel>Notes</SectionLabel>
                    <Box
                      sx={{
                        p: "10px 12px",
                        bgcolor: ev.status === "Success" ? "#f8fafc" : "#fef2f2",
                        border: `1px solid ${ev.status === "Success" ? "#e2e8f0" : "#fecaca"}`,
                        borderRadius: "6px",
                        fontSize: 13,
                        color: ev.status === "Success" ? "#334155" : "#991b1b",
                        fontFamily: "monospace",
                        lineHeight: 1.6,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {ev.reason || "No notes recorded for this step."}
                    </Box>

                    {ev.requestBody && (
                      <Box sx={{ mt: "10px" }}>
                        <SectionLabel>Request Body</SectionLabel>
                        <Box
                          sx={{
                            p: "10px 12px",
                            bgcolor: "#fff",
                            border: "1px solid #e4e4e7",
                            borderRadius: "6px",
                            fontSize: 13,
                            fontFamily: "monospace",
                            lineHeight: 1.5,
                            maxHeight: 280,
                            overflow: "auto",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {prettyBody(ev.requestBody)}
                        </Box>
                      </Box>
                    )}

                    {ev.shopifyUrl && (
                      <Box
                        component="a"
                        href={ev.shopifyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          mt: "12px",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                          fontSize: 13,
                          color: "#1d4ed8",
                          textDecoration: "none",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        Open in Shopify <OpenInNewIcon sx={{ fontSize: 15 }} />
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function SectionLabel({ children }) {
  return (
    <Box sx={{ fontSize: 12, fontWeight: 600, color: "#71717a", textTransform: "uppercase", letterSpacing: "0.04em", mb: "5px" }}>
      {children}
    </Box>
  );
}

function Meta({ label, value, mono }) {
  return (
    <Box>
      <Box sx={{ fontSize: 11, fontWeight: 600, color: "#a1a1aa", textTransform: "uppercase", letterSpacing: "0.04em", mb: "2px" }}>
        {label}
      </Box>
      <Box sx={{ fontSize: 13, color: "#3f3f46", fontFamily: mono ? "monospace" : "inherit", wordBreak: "break-word" }}>
        {value}
      </Box>
    </Box>
  );
}
